/* eslint-disable react/no-array-index-key */

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createGlobalStyle } from 'styled-components';
import { Carousel } from 'react-responsive-carousel';
import { Pagination, Text } from '@oneaudi/unified-web-components';
import { useInViewEffect, useMediaQuery } from '@oneaudi/feature-app-utils';
import sliderCss from 'react-responsive-carousel/lib/styles/carousel.css';
import { UeContainer, UeReference } from '@oneaudi/falcon-tools';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import AssetSlide from './AssetSlide';
import { useTrackingManager } from '../utils/use-tracking-manager';
import TextSlide from './TextSlide';
import { useI18n, useMatchI18nWithValues } from '../i18n';
import { ContentContext } from '../context/ContentContext';
import {
  attachHorizontalNavigation,
  handleAssetContainerFocusTrap,
  handleTextContainerFocusTrap,
} from '../utils/a11y';

import {
  AssetsCarousel,
  Background,
  CarouselSection,
  Gallery,
  SkipButton,
  SkipSliderContainer,
  StyledPagination,
  TextCarousel,
} from './Styles';

export const SliderStyles = createGlobalStyle`

${sliderCss}
  // needed override to make flexbox children 100% height of their parent
  .carousel-root {
    height: 100%;
  }
  .carousel {
    height: 100%;
  }
  .slider-wrapper {
    height: 100%;
  }
  .slider {
    height: 100%;
    backface-visibility: hidden;
  }
  .carousel .slide {
    height: 100%;
    background: transparent;
    text-align: inherit;
  }
`;

const getValidSlideNumber = (slideNo: number, numOfSlides: number): number => {
  if (slideNo >= numOfSlides) {
    // clicked next on last slide
    return slideNo % numOfSlides;
  }
  if (slideNo < 0) {
    // clicked left on first slide
    return numOfSlides + slideNo;
  }
  // value fits
  return slideNo;
};
function getLastFocusableElement(container: HTMLElement): HTMLElement | null {
  const focusableSelectors = [
    'a[href]',
    'button:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ];
  const focusable = container.querySelectorAll<HTMLElement>(focusableSelectors.join(','));
  return focusable.length > 0 ? focusable[focusable.length - 1] : null;
}

const GallerySection = () => {
  const { slides, theme } = useContext(ContentContext);
  if (!slides) return null;
  const [activeSlide, setActiveSlide] = useState(0);
  const [previousSlide, setPreviousSlide] = useState(0);
  const [swipeEvent, setSwipeEvent] = useState(false);
  const activeSlideRef = useRef(activeSlide);
  const productHighlightsRef = useRef<HTMLDivElement>(null);
  const textSlideRefs = React.useRef<(HTMLDivElement | null)[]>([]);
  const assetSlideRef = React.useRef<(HTMLDivElement | null)[]>([]);
  const paginationRef = useRef<HTMLDivElement>(null);
  const skipTargetRef = useRef<HTMLDivElement>(null);

  const prevBtnRef = useRef<HTMLButtonElement>(null);
  const nextBtnRef = useRef<HTMLButtonElement>(null);
  const [onEnter, setOnEnter] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const focusAssetFirst = isMobile && isPortrait;
  const [focusedFromArrow, setFocusedFromArrow] = useState(false);

  // i18n translations
  const skipLinkText = useI18n('skipLinkText');
  const skipTargetText = useI18n('skipTargetText');

  const textCarouselAria = useI18n('textCarouselAria');
  const assetCarouselAria = useI18n('assetCarouselAria');
  const carouselNavigationAria = useI18n('carouselNavigationAria');

  const matchI18nWithValues = useMatchI18nWithValues();

  const prevAria = useI18n('prevAria');
  const nextAria = useI18n('nextAria');
  const prevDisabledText = useI18n('prevDisabledAria');
  const nextDisabledText = useI18n('nextDisabledAria');

  const prevSlideLabel = activeSlide > 0 ? prevAria : prevDisabledText;

  const nextSlideLabel = activeSlide < slides.length - 1 ? nextAria : nextDisabledText;

  const featureAppId = __FEATURE_APP_NAME__;

  const trackingManager = useTrackingManager();
  useEffect(() => {
    activeSlideRef.current = activeSlide;
  }, [activeSlide]);
  // track navigation
  const trackNavigate = (
    fromSlideNo: number,
    targetSlideNo: number,
    clickedElemLabel: string,
    clickId: string,
  ) => {
    const fromNo = getValidSlideNumber(fromSlideNo, slides.length);
    const targetNo = getValidSlideNumber(targetSlideNo, slides.length);
    trackingManager.navigateGallery(
      slides[targetNo]?.headline,
      slides[targetNo]?.asset?.assetType || 'image',
      clickedElemLabel,
      clickId,
      fromNo + 1,
      targetNo + 1,
    );
  };

  React.useEffect(() => {
    trackingManager.loaded();
  }, []);

  useInViewEffect(() => {
    trackingManager.impression(slides[0]?.headline, slides[0]?.asset?.assetType || 'image');
  });

  useEffect(() => {
    if (swipeEvent) {
      trackNavigate(
        previousSlide,
        activeSlide,
        'image',
        `${featureAppId} Slide ${activeSlide + 1}`,
      );
      setSwipeEvent(false);
    }
  }, [activeSlide]);

  // trap focus between the active text and asset slide,
  const applyFocusAndTrap = useCallback(
    (index: number, isMobileView: boolean) => {
      const currentTextSlide = textSlideRefs.current[index];
      const currentAssetSlide = assetSlideRef.current[index];
      const prevBtn = prevBtnRef.current;
      const nextBtn = nextBtnRef.current;

      if (!currentTextSlide || !currentAssetSlide || !prevBtn || !nextBtn) {
        return () => {};
      }

      // Array to store cleanup functions for removing event listeners later
      const cleanupFns: (() => void)[] = [];

      // If focus was not triggered by arrow key navigation
      if (!focusedFromArrow) {
        if (focusAssetFirst) {
          // if we are on mobile focus on the asset container first
          currentAssetSlide.focus({ preventScroll: true });
        } else {
          // desktop -> focus the text container first
          currentTextSlide.focus({ preventScroll: true });

          // On desktop: check for last focusable element in text container
          const lastFocusable = getLastFocusableElement(currentTextSlide);

          if (lastFocusable) {
            // If there is a last focusable element, attach a one-time listener
            // to intercept Tab key navigation to move focus to asset container
            const handleKeyDown = (e: KeyboardEvent) => {
              if (e.key === 'Tab' && !e.shiftKey) {
                e.preventDefault();
                currentAssetSlide.focus({ preventScroll: true });
              }
            };
            lastFocusable.addEventListener('keydown', handleKeyDown, { once: true });
            cleanupFns.push(() => lastFocusable.removeEventListener('keydown', handleKeyDown));
          } else {
            // If there are no focusable elements, skip directly to asset container
            currentAssetSlide.focus({ preventScroll: true });
          }
        }
      }

      // Attach focus trap logic inside the asset container to handle looping or moving focus
      const assetFocusHandler = (e: KeyboardEvent) => {
        handleAssetContainerFocusTrap(
          e,
          currentAssetSlide,
          currentTextSlide,
          nextBtn,
          prevBtn,
          isMobileView,
        );
      };
      currentAssetSlide.addEventListener('keydown', assetFocusHandler);
      cleanupFns.push(() => currentAssetSlide.removeEventListener('keydown', assetFocusHandler));

      // Attach focus trap logic inside the text container
      const textFocusHandler = (e: KeyboardEvent) => {
        handleTextContainerFocusTrap(e, currentTextSlide, nextBtn, prevBtn);
      };
      currentTextSlide.addEventListener('keydown', textFocusHandler);
      cleanupFns.push(() => currentTextSlide.removeEventListener('keydown', textFocusHandler));

      // Return a cleanup function that will remove all added event listeners when slide changes/unmounts
      return () => {
        cleanupFns.forEach((fn) => fn());
      };
    },
    [focusAssetFirst, focusedFromArrow],
  );

  const cleanupRef = useRef<() => void>();

  useEffect(() => {
    if (!onEnter) {
      return undefined;
    }
    cleanupRef.current?.();
    cleanupRef.current = applyFocusAndTrap(activeSlide, isMobile);

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = undefined;
    };
  }, [activeSlide, focusAssetFirst, focusedFromArrow]);

  useEffect(() => {
    if (!onEnter) return;
    applyFocusAndTrap(activeSlide, isMobile);
  }, [activeSlide]);

  // ARROW NAV
  useEffect(() => {
    const skipElement = skipTargetRef.current;
    const sliderPagination = paginationRef.current;

    if (!sliderPagination || !skipElement) return undefined;

    // attach horizontal navigation
    attachHorizontalNavigation(paginationRef.current);

    // skip to next section
    const skipSlider = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (sliderPagination.contains(document.activeElement)) {
          e.preventDefault();
          skipElement.focus();
        }
      }
    };

    document.addEventListener('keydown', skipSlider);

    return () => {
      document.removeEventListener('keydown', skipSlider);
    };
  }, []);

  // ESC key skip section
  useEffect(() => {
    const productHighlights = productHighlightsRef.current;
    const targetEl = skipTargetRef.current;

    if (!productHighlights || !targetEl) return undefined;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Check if the currently focused element is inside the Product Highlights
        if (productHighlights.contains(document.activeElement)) {
          e.preventDefault();
          targetEl.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const renderTextCarousel = () => (
    <CarouselSection>
      <Carousel
        selectedItem={activeSlide}
        onChange={setActiveSlide}
        showArrows={false}
        showIndicators={false}
        showStatus={false}
        showThumbs={false}
        infiniteLoop={false}
        autoPlay
        interval={3600000}
        swipeScrollTolerance={80}
        preventMovementUntilSwipeScrollTolerance
        useKeyboardArrows
      >
        {slides.map((slide, index) => (
          <TextSlide
            key={`text_${slide.headline}`}
            content={slide}
            active={index === activeSlide}
            testId={`textslide-${index}`}
            index={index}
            ref={(el) => {
              textSlideRefs.current[index] = el;
            }}
          />
        ))}
      </Carousel>
    </CarouselSection>
  );

  const renderAssetCarousel = () => (
    <UeContainer propertyPath="slides" label="Slides">
      <Carousel
        selectedItem={activeSlide}
        onChange={setActiveSlide}
        onSwipeEnd={() => {
          setPreviousSlide(activeSlide);
          setSwipeEvent(true);
        }}
        showArrows={false}
        showIndicators={false}
        showStatus={false}
        showThumbs={false}
        dynamicHeight={false}
        infiniteLoop={false}
        autoPlay
        interval={3600000}
        swipeScrollTolerance={80}
        preventMovementUntilSwipeScrollTolerance
        useKeyboardArrows
        aria-live="polite"
      >
        {slides.map((slide, idx) => (
          <UeReference key={`slides.${idx}`} propertyPath={`slides.${idx}`} behavior="component">
            <AssetSlide
              key={`asset_component_.${idx}`}
              content={slide}
              backgroundColor={slide.asset?.backgroundColor}
              active={idx === activeSlide}
              index={idx}
              ref={(el) => {
                assetSlideRef.current[idx] = el;
              }}
            />
          </UeReference>
        ))}
      </Carousel>
    </UeContainer>
  );

  const renderPagination = () => (
    <StyledPagination ref={paginationRef} role="navigation">
      <Pagination
        aria-label={carouselNavigationAria}
        className="product-highlights-pagination"
        variant="extended"
        current={activeSlide + 1}
        itemCount={slides.length}
        decrementElement={{
          ref: prevBtnRef,
          'aria-label': prevSlideLabel,
          'aria-disabled': activeSlide === 0,
          onClick: () => handleSlideChange(activeSlide - 1, 'ArrowLeft', true),
          onKeyDown: (e) =>
            e.key === 'Enter' && handleSlideChange(activeSlide - 1, 'ArrowLeft', true),
        }}
        incrementElement={{
          ref: nextBtnRef,
          'aria-label': nextSlideLabel,
          'aria-disabled': activeSlide === slides.length - 1,
          onClick: () => handleSlideChange(activeSlide + 1, 'ArrowRight', true),
          onKeyDown: (e) =>
            e.key === 'Enter' && handleSlideChange(activeSlide + 1, 'ArrowRight', true),
        }}
        items={slides.map((_slide, index) => ({
          'aria-label': matchI18nWithValues('jump.to', {
            slideNo: (index + 1).toString(),
          }),
          onClick: () => handleSlideChange(index, `Pos ${index + 1}`, false),
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              if (index === activeSlide && focusedFromArrow) {
                // If this is the second Enter press after arrow navigation
                const textSlide = textSlideRefs.current[index];
                if (textSlide) {
                  textSlide.focus({ preventScroll: true });
                  setFocusedFromArrow(false); // Reset the flag
                }
              } else {
                handleSlideChange(index, `Pos ${index + 1}`, false);
              }
            }
          },
        }))}
        data-testid="pagination"
      />
    </StyledPagination>
  );

  const handleSlideChange = (index: number, trackingLabel: string, isArrow: boolean) => {
    trackNavigate(activeSlide, index, trackingLabel, `${featureAppId} ${trackingLabel}`);

    // Always trigger onEnter logic
    setOnEnter(true);

    // Even if slide index is the same -> refocus
    if (index !== activeSlide) {
      setActiveSlide(index);
    } else {
      requestAnimationFrame(() => {
        applyFocusAndTrap(index, isMobile);
      });
    }

    if (isArrow && paginationRef.current) {
      const buttons = Array.from(
        paginationRef.current.querySelectorAll('.product-highlights-pagination button'),
      ) as HTMLButtonElement[];
      const targetButton = buttons[index + 1]; // +1 to skip prev button
      if (targetButton) {
        requestAnimationFrame(() => {
          targetButton.focus({ preventScroll: true });
          setFocusedFromArrow(true);
        });
      }
    } else {
      setFocusedFromArrow(false);
    }
  };

  // since we don't have direct access to Carousel, we need to
  // manually attach role and aria-label to Carousel
  useEffect(() => {
    const carouselRoots = productHighlightsRef?.current?.querySelectorAll('.carousel-root');
    if (!productHighlightsRef || !carouselRoots) return;

    carouselRoots.forEach((carousel, index) => {
      carousel.setAttribute('role', 'region');
      carousel.setAttribute('aria-label', index === 0 ? assetCarouselAria : textCarouselAria);
    });
  }, []);

  return (
    <ThemeProvider theme={{ name: theme ?? 'light' }}>
      <Background data-testid="gallery-section">
        <SliderStyles />
        <Gallery ref={productHighlightsRef}>
          <AssetsCarousel key="asset-mobile">{renderAssetCarousel()}</AssetsCarousel>
          <TextCarousel key="text-mobile">
            <SkipButton
              size="sm"
              variant="tertiary"
              onClick={() => {
                skipTargetRef.current?.focus();
              }}
            >
              {skipLinkText}
            </SkipButton>
            {renderPagination()}
            {renderTextCarousel()}
          </TextCarousel>
        </Gallery>
        <SkipSliderContainer ref={skipTargetRef} tabIndex={-1} aria-hidden="true">
          <Text variant={{ type: 'copy', order: '3', style: 'normal' }}>{skipTargetText}</Text>
        </SkipSliderContainer>
      </Background>
    </ThemeProvider>
  );
};

export default GallerySection;
